"use client";

import React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "../components/ui/button";
import {
  MoreHorizontal,
  Edit,
  Trash2,
  ArrowUpDown,
  Copy,
  Check,
  UserX,
  UserCheck,
  ArrowUp,
} from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { useState } from "react";
import { ConfirmationDialog } from "../components/confirmationDialog";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import { UpgradeEmployeeDialog } from "../components/upgradeEmployeeDialog";
import axios from "axios";

// Updated Employee type with off-boarding fields
export type Employee = {
  id: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  employeeType?: "Employee" | "Intern" | "Part-Time";
  isActive: boolean;
  resignationDate?: string;
  resignationReason?: string;
  lastWorkingDay?: string;
};

// Delete function
const deleteEmployee = async (employeeId: string) => {
  const token = localStorage.getItem("authToken");
  await axios.delete(
    `${process.env.REACT_APP_BACKEND_URL}api/employees/${employeeId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
};

// Resign employee function
const resignEmployee = async (
  employeeId: string,
  resignationReason?: string,
  lastWorkingDay?: string
) => {
  const token = localStorage.getItem("authToken");
  await axios.post(
    `${process.env.REACT_APP_BACKEND_URL}api/employees/${employeeId}/resign`,
    {
      resignationReason,
      lastWorkingDay,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
};

// Reactivate employee function
const reactivateEmployee = async (employeeId: string) => {
  const token = localStorage.getItem("authToken");
  await axios.post(
    `${process.env.REACT_APP_BACKEND_URL}api/employees/${employeeId}/reactivate`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
};

// Individual delete confirmation component
const DeleteEmployeeDialog = ({
  employee,
  onSuccess,
  isOpen,
  onClose,
}: {
  employee: Employee;
  onSuccess: () => void;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleDelete = async () => {
    try {
      setIsLoading(true);
      await deleteEmployee(employee.id);
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Failed to delete employee:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={handleDelete}
      title={`Delete ${employee.employeeType || "Employee"}`}
      description={`Are you sure you want to delete ${employee.name}? This action cannot be undone.`}
      confirmText={isLoading ? "Deleting..." : "Delete"}
      isLoading={isLoading}
    />
  );
};

// Resign employee confirmation component
const ResignEmployeeDialog = ({
  employee,
  onSuccess,
  isOpen,
  onClose,
}: {
  employee: Employee;
  onSuccess: () => void;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleResign = async () => {
    try {
      setIsLoading(true);
      await resignEmployee(employee.id);
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Failed to resign employee:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={handleResign}
      title="Mark Employee as Resigned"
      description={`Are you sure you want to mark ${employee.name} as resigned? This will make them inactive in the system.`}
      confirmText={isLoading ? "Processing..." : "Mark as Resigned"}
      isLoading={isLoading}
    />
  );
};

// Reactivate employee confirmation component
const ReactivateEmployeeDialog = ({
  employee,
  onSuccess,
  isOpen,
  onClose,
}: {
  employee: Employee;
  onSuccess: () => void;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleReactivate = async () => {
    try {
      setIsLoading(true);
      await reactivateEmployee(employee.id);
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Failed to reactivate employee:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={handleReactivate}
      title="Reactivate Employee"
      description={`Are you sure you want to reactivate ${employee.name}? This will make them active again in the system.`}
      confirmText={isLoading ? "Processing..." : "Reactivate"}
      isLoading={isLoading}
    />
  );
};

// Actions cell component
const ActionsCell = ({
  employee,
  onEmployeeDeleted,
}: {
  employee: Employee;
  onEmployeeDeleted: () => void;
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resignDialogOpen, setResignDialogOpen] = useState(false);
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  const handleEdit = async () => {
    setIsNavigating(true);
    await new Promise((resolve) => setTimeout(resolve, 200));
    window.location.href = `/employees/edit/${employee.id}`;
  };

  const canUpgrade = employee.employeeType === "Intern" || employee.employeeType === "Part-Time";

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="h-8 w-8 p-0"
            disabled={isNavigating}
          >
            <span className="sr-only">Open menu</span>
            {isNavigating ? (
              <Spinner variant="ellipsis" size={16} />
            ) : (
              <MoreHorizontal className="h-4 w-4" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleEdit} disabled={isNavigating}>
            <Edit className="mr-2 h-4 w-4" />
            Edit
          </DropdownMenuItem>
          {canUpgrade && (
            <DropdownMenuItem onClick={() => setUpgradeDialogOpen(true)}>
              <ArrowUp className="mr-2 h-4 w-4" />
              Upgrade to Full-Time
            </DropdownMenuItem>
          )}
          {employee.isActive ? (
            <DropdownMenuItem onClick={() => setResignDialogOpen(true)}>
              <UserX className="mr-2 h-4 w-4" />
              Mark as Resigned
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={() => setReactivateDialogOpen(true)}>
              <UserCheck className="mr-2 h-4 w-4" />
              Reactivate Employee
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setDeleteDialogOpen(true)}
            className="text-red-600"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteEmployeeDialog
        employee={employee}
        onSuccess={onEmployeeDeleted}
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      />

      <ResignEmployeeDialog
        employee={employee}
        onSuccess={onEmployeeDeleted}
        isOpen={resignDialogOpen}
        onClose={() => setResignDialogOpen(false)}
      />

      <ReactivateEmployeeDialog
        employee={employee}
        onSuccess={onEmployeeDeleted}
        isOpen={reactivateDialogOpen}
        onClose={() => setReactivateDialogOpen(false)}
      />

      <UpgradeEmployeeDialog
        employee={employee}
        isOpen={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
        onSuccess={onEmployeeDeleted}
      />
    </>
  );
};

// Email cell component with copy functionality
const EmailCell = ({ email }: { email: string }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(email);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="group flex items-center gap-2 text-left">
      <span>{email}</span>
      <button
        onClick={copyToClipboard}
        className="opacity-50 group-hover:opacity-100 transition-opacity hover:opacity-100"
        title="Copy email"
      >
        {copied ? (
          <Check className="h-3 w-3 text-green-600" />
        ) : (
          <Copy className="h-3 w-3 text-muted-foreground hover:text-foreground" />
        )}
      </button>
    </div>
  );
};

// Helper function for employee type badge colors
const getEmployeeTypeBadgeColor = (type?: string) => {
  switch (type) {
    case "Intern":
      return "bg-blue-100 text-blue-800 border-blue-200";
    case "Part-Time":
      return "bg-purple-100 text-purple-800 border-purple-200";
    case "Employee":
    default:
      return "bg-green-100 text-green-800 border-green-200";
  }
};

// Column definitions factory function
export const createEmployeeColumns = (
  onEmployeeDeleted: () => void
): ColumnDef<Employee>[] => [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected()}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "name",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Name
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const name = row.getValue("name") as string;
      return <div className="font-medium text-left">{name}</div>;
    },
  },
  {
    accessorKey: "email",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Email
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const email = row.getValue("email") as string;
      return <EmailCell email={email} />;
    },
  },
  {
    accessorKey: "phone",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Phone
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const phone = row.getValue("phone") as string;
      return <div className="text-left">{phone || "N/A"}</div>;
    },
  },
  {
    accessorKey: "employeeType",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Type
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const type = (row.getValue("employeeType") as string) || "Employee";
      return (
        <div className="text-left">
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors ${getEmployeeTypeBadgeColor(
              type
            )}`}
          >
            {type}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "company",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Company
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const company = row.getValue("company") as string;
      return (
        <div className="text-left">
          {company ? (
            <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80">
              {company}
            </span>
          ) : (
            <span className="text-muted-foreground">N/A</span>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "isActive",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Status
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const isActive = row.getValue("isActive") as boolean;
      const resignationDate = row.original.resignationDate;

      return (
        <div className="text-left">
          {isActive ? (
            <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors border-transparent bg-green-100 text-green-800">
              Active
            </span>
          ) : (
            <div className="space-y-1">
              <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors border-transparent bg-red-100 text-red-800">
                Inactive
              </span>
              {resignationDate && (
                <div className="text-xs text-muted-foreground">
                  Resigned: {new Date(resignationDate).toLocaleDateString()}
                </div>
              )}
            </div>
          )}
        </div>
      );
    },
    sortingFn: (rowA, rowB) => {
      const statusA = rowA.getValue("isActive") as boolean;
      const statusB = rowB.getValue("isActive") as boolean;
      if (statusA && !statusB) return -1;
      if (!statusA && statusB) return 1;
      return 0;
    },
  },
  {
    id: "actions",
    header: () => <div className="text-left">Actions</div>,
    cell: ({ row }) => {
      const employee = row.original;
      return (
        <ActionsCell
          employee={employee}
          onEmployeeDeleted={onEmployeeDeleted}
        />
      );
    },
  },
];

// Keep the original columns export for backward compatibility
export const columns = createEmployeeColumns(() => {});
